import { faArrowLeft, faCalendar, faClock, faUser } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import useAuth from '@hooks/useAuth';
import { Button } from '@ui';
import LoaderBar from '@ui/LoaderBar';
import { camelCase, snakeCase } from 'lodash';
import moment from 'moment';
import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { cn } from '~/lib/cn';
import type { StepState } from '../../Form/utilities/FormSteps';
import type { ExtendedSlot } from '../Views/TableView/types';
import BookingSummary from './BookingSummary';
import useBookingCheck from './useBookingCheck';

interface Props {
    slot: ExtendedSlot;
    spaceName?: string;
    kiosk?: boolean;
    summaryEndpoint?: string;
    checkEndpoint?: string;
    requestData?: Record<string, any>;
    /** false turns the rule check off entirely (the assistant's showRuleChecker prop) */
    checkRules?: boolean;
    step: StepState | null;
    isLoading?: boolean;
    hasOnlinePayments?: boolean;
    /** Explicit submit label from the host, overriding every derived one */
    submitTextOverride?: string;
    onRequiresApprovalChange?: (requiresApproval: boolean) => void;
    onPaymentRuleChange?: (onlinePayments: boolean | undefined, paymentMethod: string | undefined) => void;
    onNext: () => void;
    onBack: () => void;
}

/**
 * The booking's standing context: what is being booked, what is wrong with it, what it
 * costs, and the button that orders it. Lives beside the wizard rather than inside it so
 * the price is visible from the first step instead of only on the last one.
 *
 * Must be rendered inside the assistant's Form — it reads the booking straight off it.
 */
export default function BookingRail({
    slot,
    spaceName,
    kiosk,
    summaryEndpoint,
    checkEndpoint,
    requestData,
    checkRules = true,
    step,
    isLoading,
    hasOnlinePayments,
    submitTextOverride,
    onRequiresApprovalChange,
    onPaymentRuleChange,
    onNext,
    onBack
}: Props) {
    const { t } = useTranslation('space-manager');
    const { watch } = useFormContext();
    const { user } = useAuth();
    const {
        errors,
        forbidMessage,
        messages,
        requiresApproval,
        onlinePayments,
        paymentMethod,
        isLoading: isChecking
    } = useBookingCheck({
        slot,
        kiosk,
        enabled: checkRules,
        endpoint: checkEndpoint
    });

    useEffect(() => {
        onRequiresApprovalChange?.(requiresApproval);
    }, [requiresApproval, onRequiresApprovalChange]);

    useEffect(() => {
        onPaymentRuleChange?.(onlinePayments, paymentMethod);
    }, [onlinePayments, paymentMethod, onPaymentRuleChange]);

    const start = watch('start');
    const end = watch('end');
    const bookerName = watch('user_name') || user?.name;
    const hasRuleError = Boolean(forbidMessage) || errors.length > 0;
    // die Navigation wird nie gesperrt: die meisten Regelverstöße lassen sich erst auf einem
    // späteren Schritt beheben (Mitbucher, Addons, Login) — nur die Bestellung selbst wartet
    const blocksSubmit = hasRuleError && Boolean(step?.isLast);
    const duration = start && end ? moment(end).diff(moment(start), 'minutes') : slot.width;

    // no amount on the button: the total sits right above it in the summary
    const submitText = () => {
        if (submitTextOverride) return submitTextOverride;
        if (requiresApproval) return t('bookingAssistant.requestSubmitText');
        return hasOnlinePayments
            ? t('bookingAssistant.paySubmitText')
            : t('bookingAssistant.defaultSubmitText');
    };

    const footnote = requiresApproval
        ? t('bookingAssistant.requestNotice')
        : hasOnlinePayments
          ? t('bookingAssistant.paymentRedirectNotice')
          : undefined;

    return (
        <aside className="tw-flex tw-flex-col tw-border-0 tw-border-solid tw-border-accents-2 tw-bg-accents-1 lg:tw-min-h-0 lg:tw-w-[340px] lg:tw-shrink-0 lg:tw-border-l">
            <div className="tw-flex tw-flex-1 tw-flex-col tw-gap-4 tw-p-6 lg:tw-min-h-0 lg:tw-overflow-y-auto">
                <LoaderBar
                    className={cn(isChecking ? 'tw-opacity-100' : 'tw-opacity-0', 'tw-h-1 tw-transition-opacity')}
                />

                <span className="tw-text-xs tw-font-bold tw-uppercase tw-tracking-wide tw-text-accents-6">
                    {t('bookingAssistant.rail.title')}
                </span>

                <div className="tw-card-bordered tw-flex tw-flex-col tw-gap-1">
                    <span className="tw-font-semibold tw-text-accents-9">{spaceName ?? `#${String(slot.spaceId)}`}</span>
                    <span className="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-text-accents-6">
                        <FontAwesomeIcon icon={faCalendar} className="tw-w-3" />
                        {moment(start ?? slot.currentDate).format('dd, DD.MM.YY')}
                    </span>
                    <span className="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-text-accents-6">
                        <FontAwesomeIcon icon={faClock} className="tw-w-3" />
                        {start && end
                            ? `${moment(start).format('HH:mm')} – ${moment(end).format('HH:mm')} · ${duration} Min`
                            : `${duration} Min`}
                    </span>
                    {bookerName && (
                        <>
                            <div className="tw-my-2 tw-h-px tw-bg-accents-2" />
                            <span className="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-text-accents-6">
                                <FontAwesomeIcon icon={faUser} className="tw-w-3" />
                                {bookerName}
                            </span>
                        </>
                    )}
                </div>

                {hasRuleError && (
                    <div
                        className={cn(
                            'tw-card-flat',
                            // rot erst dort, wo der Fehler wirklich die Bestellung aufhält
                            blocksSubmit ? 'tw-bg-errorLight tw-text-error' : 'tw-bg-warningLight tw-text-accents-9'
                        )}
                    >
                        <span className="tw-font-bold">
                            {forbidMessage ??
                                t(
                                    blocksSubmit
                                        ? 'bookingAssistant.rail.errorTitle'
                                        : 'bookingAssistant.rail.pendingErrorTitle'
                                )}
                        </span>
                        {!forbidMessage && (
                            <ul className="tw-mb-0 tw-mt-1 tw-flex tw-flex-col tw-gap-0.5 tw-pl-4 tw-text-sm">
                                {errors.map((error) => (
                                    <li key={snakeCase(error)}>{error}</li>
                                ))}
                            </ul>
                        )}
                    </div>
                )}
                {messages.length > 0 && (
                    <div className="tw-card-bordered tw-flex tw-flex-col tw-gap-1 tw-text-sm tw-text-accents-6">
                        {messages.map((message) => (
                            <span className="tw-font-semibold" key={camelCase(message)}>
                                {message}
                            </span>
                        ))}
                    </div>
                )}
                <BookingSummary
                    kiosk={kiosk}
                    slot={slot}
                    summaryEndpoint={summaryEndpoint}
                    requestData={requestData}
                    pendingPlaceholder={t('bookingAssistant.rail.pricePending')}
                />
            </div>

            <div className="tw-sticky tw-bottom-0 tw-flex tw-flex-col tw-gap-3 tw-border-0 tw-border-t tw-border-solid tw-border-accents-2 tw-bg-accents-1 tw-p-6">
                <div className="tw-flex tw-gap-2">
                    {step && !step.isFirst && (
                        <Button type="button" color="secondary" auto disabled={isLoading} onPress={onBack}>
                            <FontAwesomeIcon icon={faArrowLeft} />
                        </Button>
                    )}
                    <Button
                        type="button"
                        block
                        auto
                        isLoading={isLoading}
                        isDisabled={blocksSubmit || isLoading}
                        disabled={blocksSubmit || isLoading}
                        onPress={onNext}
                    >
                        {step?.isLast ? submitText() : 'Weiter'}
                    </Button>
                </div>
                {step?.isLast && footnote && (
                    <span className="tw-text-xs tw-text-accents-6">{footnote}</span>
                )}
            </div>
        </aside>
    );
}
